import React, { useEffect, useMemo, useState } from 'react'
import { useToggleSuggestions } from 'src/components/async/suggestions/useToggleSuggestions'
import { TextButton } from 'src/components/Reusable/Buttons/TextButton'
import {
  SmallStyledSubsectionAccordionIcon,
  StyledSubsectionAccordionIcon,
  SubsectionAccordionEvidenceIcon,
  SubsectionAccordionTestIcon,
} from 'src/components/Reusable/Accordions/ShowPage/Subsections/SubsectionAccordion.styles'
import {
  faRefresh,
  faRotateLeft,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import { AccordionEventKey } from 'react-bootstrap/esm/AccordionContext'
import showdown from 'showdown'
import {
  GetSuggestedRequest,
  SUGGESTION_TYPES,
} from '@trustero/trustero-api-web/lib/recommend/recommend_pb'
import {
  SuggestionsObject,
  getDedupedSuggestionsObject,
  convertSuggestionsToText,
  getCustomSuggestionText,
} from 'src/components/async/suggestions/suggestions.helpers'
import { TabType, Tabs } from 'src/components/Reusable/Tabs'
import { Markup } from 'src/components/Reusable/Text/Markup'
import { useAnalytics } from 'src/analytics/useAnalytics'
import { useSuggestedTestTable } from 'src/components/async/suggestions/useSuggestedTests'
import { useSuggestedEvidenceTable } from 'src/components/async/suggestions/useSuggestedEvidence'
import { faComment } from '@fortawesome/free-regular-svg-icons'
import { HUBSPOT } from 'src/Utils/hubspot/hubspot.utils'
import { WarningP } from 'src/components/ModalForms/ModalForm.styles'
import { useTypewriter } from 'src/Utils/globalHooks'
import { getAILoadingMessage } from 'src/Utils/globalHelpers'
import { Message } from 'src/components/Reusable/Message/Message'
import { MessageType } from 'src/components/Reusable/Message/Message.constants'
import { SafeEditField } from 'src/components/Reusable/Inputs/SafeEditField'
import { GRC_PLATFORM } from '@trustero/trustero-api-web/lib/common/grc_platform_pb'
import { useImportedControlModalOnClick } from 'src/pages/Controls/ControlsIndexGrid.hooks'
import { CONTROL_READ_ONLY_FIELDS } from '@trustero/trustero-api-web/lib/model/control_pb'
import 'react-mde/lib/styles/css/react-mde-all.css'
import { ControlSubsectionAccordionItemComponent } from '../ControlsShowItemAccordions.components'
import {
  useUpdateSuggestedTests,
  useUpdateSuggestedEvidence,
} from '../../../ControlShowPage.hooks'
import {
  AICopilotGuidanceContainer,
  RestoreGuidanceContainer,
  GuidanceAICopilotIconEast,
  RegularGuidanceLoadingContainer,
} from './ControlsShowItemGuidance.styles'
import { getNotesCopy } from './ControlsShowItemGuidance.helpers'

const INCOMPLETE_SCOPING_GUIDANCE_ANSWER =
  'Tailored guidance needs to know which services you use. Complete scoping.'

const converter = new showdown.Converter()
converter.setFlavor('github')
converter.setOption('simpleLineBreaks', false)

export const SubsectionAccordionMagicIcon = ({
  className,
}: {
  className?: string
}): JSX.Element => {
  return (
    <StyledSubsectionAccordionIcon
      icon={faWandMagicSparkles}
      className={className}
    />
  )
}

export const SubsectionAccordionCommentIcon = ({
  className,
}: {
  className?: string
}): JSX.Element => {
  return (
    <StyledSubsectionAccordionIcon icon={faComment} className={className} />
  )
}

export const SubsectionAccordionRestoreIcon = (): JSX.Element => {
  return <SmallStyledSubsectionAccordionIcon icon={faRotateLeft} />
}

export const SubsectionAccordionRefreshIcon = (): JSX.Element => {
  return <SmallStyledSubsectionAccordionIcon icon={faRefresh} $isRefresh />
}

export const RestoreGuidance = ({
  controlId,
  updateRestore,
}: {
  controlId: string
  updateRestore: () => void
}): JSX.Element => {
  const { restoreSuggestions } = useToggleSuggestions({ controlId })
  const { track, events } = useAnalytics()
  return (
    <RestoreGuidanceContainer>
      <TextButton
        onClick={async (e) => {
          e.stopPropagation()
          track(events.C_SUGG_RESTORE_GUIDANCE, { controlId: controlId })
          await restoreSuggestions()
          updateRestore()
        }}
      >
        <>
          <SubsectionAccordionRestoreIcon />
          Restore Guidance
        </>
      </TextButton>
    </RestoreGuidanceContainer>
  )
}

export const TypewriterMarkup = ({
  text,
  customConverter,
}: {
  text: string
  customConverter?: showdown.Converter
}): JSX.Element => {
  const typed = useTypewriter({ text })
  return <Markup markdown={typed} customConverter={customConverter} />
}

export const SubsectionSuggestionBody = ({
  value,
  isAI,
  showTyping,
  customElement,
  customMdConverter,
}: {
  value: string
  isAI: boolean
  showTyping: boolean
  customElement?: JSX.Element
  customMdConverter?: showdown.Converter
}): JSX.Element => (
  <>
    {customElement ? (
      customElement
    ) : isAI ? (
      <AICopilotGuidanceContainer>
        <GuidanceAICopilotIconEast />
        {showTyping ? (
          <TypewriterMarkup text={value} customConverter={customMdConverter} />
        ) : (
          <Markup markdown={value} customConverter={customMdConverter} />
        )}
      </AICopilotGuidanceContainer>
    ) : (
      <Markup markdown={value} customConverter={customMdConverter} />
    )}
  </>
)

export const SubsectionSuggestion = ({
  suggestions,
  controlId,
  isCustom,
  isAI,
  isNewlyGenerated,
  isTestProcedures,
}: {
  suggestions: SuggestionsObject
  controlId?: string
  isCustom?: boolean
  isAI?: boolean
  isNewlyGenerated?: boolean
  isTestProcedures?: boolean
}): JSX.Element => {
  const { track, events } = useAnalytics()
  const [selectedTab, setSelectedTab] = useState<number>(0)
  const duplicates = suggestions.duplicates
  const suggestionArray = Object.entries(suggestions)
  const suggestionValue = Object.values(suggestions)
  const suggestionTabs: TabType[] = []
  const showTyping = (!isCustom && isNewlyGenerated) || false

  // Create converter with simpleLineBreaks for test procedures
  const converter = useMemo(() => {
    if (!isTestProcedures) return undefined

    const testProceduresConverter = new showdown.Converter()
    testProceduresConverter.setFlavor('github')
    testProceduresConverter.setOption('simpleLineBreaks', true)
    testProceduresConverter.setOption('simplifiedAutoLink', false)
    return testProceduresConverter
  }, [isTestProcedures])

  const [isScopingDismissed, setIsScopingDismissed] = useState(false)
  const ScopeBanner = (): JSX.Element =>
    isScopingDismissed ? (
      <></>
    ) : (
      <Message
        messageType={MessageType.INFO}
        messageText="Tailored guidance needs to know which services you use."
        size="medium"
        ctaButton={{
          text: 'Complete scoping',
          to: '../../../scope',
        }}
        onDismiss={() => setIsScopingDismissed(true)}
        fullWidth
      />
    )
  for (const [key, value] of suggestionArray) {
    if (key === 'duplicates') {
      continue
    }

    const suggestionTab = {
      title: key,
      body: (
        <SubsectionSuggestionBody
          value={value}
          isAI={isAI || false}
          showTyping={showTyping}
          customElement={
            value === INCOMPLETE_SCOPING_GUIDANCE_ANSWER
              ? ScopeBanner()
              : undefined
          }
          customMdConverter={converter}
        />
      ),
    }
    suggestionTabs.push(suggestionTab)
  }
  // If there is only one suggestion, display it alone
  const showSingular = suggestionTabs.length === 1
  // If there is more than one unique suggestion, display the tabs
  const showTabs = suggestionTabs.length > 1
  const onSelect = (e: string | null): void => {
    setSelectedTab(Number(e))
    track(events.C_SUGG_SWITCH_TABS, {
      controlId,
      tab: suggestionTabs[Number(e)].title,
    })
  }

  return (
    <>
      {isCustom ? (
        <Markup
          markdown={suggestionValue[0] || 'None'}
          customConverter={converter}
        />
      ) : (
        <>
          {duplicates && (
            <SubsectionSuggestionBody
              value={duplicates}
              isAI={isAI || false}
              showTyping={showTyping}
              customElement={
                duplicates === INCOMPLETE_SCOPING_GUIDANCE_ANSWER
                  ? ScopeBanner()
                  : undefined
              }
              customMdConverter={converter}
            />
          )}
          {showSingular && suggestionTabs[0].body}
        </>
      )}
      {!isCustom && showTabs && (
        <Tabs
          tabs={suggestionTabs}
          onSelect={onSelect}
          defaultActiveKey={selectedTab}
        />
      )}
      {!duplicates && !showSingular && !showTabs && (
        <Markup markdown="We couldn't find a match. Please add your own." />
      )}
    </>
  )
}

export const SubsectionTailoredGuidance = ({
  suggestions,
  controlId,
  isLoading,
  isRefreshing,
  isNewlyGenerated,
}: {
  suggestions: SuggestionsObject
  controlId: string
  isLoading: boolean
  isRefreshing: boolean
  isNewlyGenerated?: boolean
}): JSX.Element => {
  const [isInfoDismissed, setIsInfoDismissed] = useState(false)
  const hasScopingAnswer = Object.values(suggestions).some(
    (value) => value === INCOMPLETE_SCOPING_GUIDANCE_ANSWER,
  )

  return (
    <>
      {isRefreshing || isLoading ? (
        <AICopilotLoading />
      ) : (
        <>
          {!isInfoDismissed && !hasScopingAnswer && (
            <Message
              messageType={MessageType.INFO}
              messageText="Tailored guidance is not editable, but can be regenerated."
              size="medium"
              padding="12px"
              onDismiss={() => setIsInfoDismissed(true)}
              fullWidth
            />
          )}
          <SubsectionSuggestion
            suggestions={suggestions}
            controlId={controlId}
            isNewlyGenerated={isNewlyGenerated}
          />
        </>
      )}
    </>
  )
}

export const GuidanceLoading = (): JSX.Element => {
  const ellipsis = useTypewriter({
    text: '...',
    interval: 500,
    shouldLoop: true,
  })
  return (
    <RegularGuidanceLoadingContainer>
      Loading{ellipsis}
    </RegularGuidanceLoadingContainer>
  )
}

export const AICopilotLoading = (): JSX.Element => {
  const message = useMemo(() => getAILoadingMessage(), [])
  const ellipsis = useTypewriter({
    text: '...',
    interval: 500,
    shouldLoop: true,
  })
  return (
    <AICopilotGuidanceContainer>
      {message + ellipsis}
    </AICopilotGuidanceContainer>
  )
}

export const SubsectionAccordionSuggestedTests = ({
  eventKey,
  modelId,
  controlId,
  complianceFrameworks,
  grcPlatform,
  noEdit,
}: {
  eventKey: AccordionEventKey
  modelId: string
  controlId: string
  complianceFrameworks: string[]
  grcPlatform?: GRC_PLATFORM
  noEdit?: boolean
}): JSX.Element => {
  const updateSuggestedTests = useUpdateSuggestedTests()
  const onClickConfirmationModal = useImportedControlModalOnClick({
    grcPlatform,
    showModal: noEdit,
    controlId,
    fields: [CONTROL_READ_ONLY_FIELDS.CONTROL_PROCEDURE_MAP],
  })
  const { data, isLoading, isValidating } = useSuggestedTestTable(
    new GetSuggestedRequest()
      .setControlId(controlId)
      .setComplianceFrameworkBaseModelIdsList(complianceFrameworks || []),
  )
  const [isCustom, setIsCustom] = useState<boolean>(false)
  const [isSuggLoading, setIsSuggLoading] = useState<boolean>(false)

  const [suggestedTests, setSuggestedTests] = useState<SuggestionsObject>(
    {} as SuggestionsObject,
  )
  const refreshSuggestedTests = (sto: SuggestionsObject) => {
    setSuggestedTests(sto)
  }

  useEffect(() => {
    if (!data) {
      return
    }
    const suggestedTest = data.toObject().suggestedTest
    setIsCustom(!!(suggestedTest && suggestedTest.isCustom))

    if (suggestedTest) {
      setSuggestedTests(getDedupedSuggestionsObject([suggestedTest]))
    }
  }, [data])

  useEffect(() => {
    if (isLoading || isValidating) {
      setIsSuggLoading(true)
    } else {
      setIsSuggLoading(false)
    }
  }, [isLoading, isValidating])

  const onSave = async (newSuggestedTests: string) => {
    if (convertSuggestionsToText(suggestedTests) === newSuggestedTests) {
      return
    }

    await updateSuggestedTests({
      controlId: controlId,
      suggestedTests: newSuggestedTests,
    })
  }

  return (
    <ControlSubsectionAccordionItemComponent
      eventKey={eventKey}
      controlModelId={modelId}
      controlId={controlId}
      complianceFrameworkIds={complianceFrameworks}
      icon={<SubsectionAccordionTestIcon />}
      title="Test Procedures"
      notes={getNotesCopy(isSuggLoading, isCustom)}
      body={
        isSuggLoading ? (
          <GuidanceLoading />
        ) : (
          <SafeEditField
            value={
              isCustom
                ? getCustomSuggestionText(suggestedTests)
                : convertSuggestionsToText(suggestedTests)
            }
            onSave={onSave}
            warning={<EditGuidanceWarning />}
            sizeWarning={<TestLengthWarning />}
            overrideViewState={
              <SubsectionSuggestion
                isCustom={isCustom}
                suggestions={suggestedTests}
                controlId={controlId}
              />
            }
            onClickConfirmationModal={onClickConfirmationModal}
          />
        )
      }
      suggestionType={SUGGESTION_TYPES.TESTS}
      suggestion={suggestedTests}
      refresh={refreshSuggestedTests}
      isUpdating={isSuggLoading}
    />
  )
}

export const SubsectionAccordionSuggestedEvidence = ({
  eventKey,
  controlId,
  modelId,
  complianceFrameworks,
  grcPlatform,
  noEdit,
}: {
  eventKey: AccordionEventKey
  controlId: string
  modelId: string
  complianceFrameworks: string[]
  grcPlatform?: GRC_PLATFORM
  noEdit?: boolean
}): JSX.Element => {
  const updateSuggestedEvidence = useUpdateSuggestedEvidence()
  const onClickConfirmationModal = useImportedControlModalOnClick({
    grcPlatform,
    showModal: noEdit,
    controlId,
    fields: [CONTROL_READ_ONLY_FIELDS.CONTROL_PROCEDURE_MAP],
  })
  const { data, isLoading, isValidating } = useSuggestedEvidenceTable(
    new GetSuggestedRequest()
      .setControlId(controlId)
      .setComplianceFrameworkBaseModelIdsList(complianceFrameworks || []),
  )
  const [isSuggLoading, setIsSuggLoading] = useState<boolean>(false)

  const [suggestedEvidence, setSuggestedEvidence] = useState<SuggestionsObject>(
    {} as SuggestionsObject,
  )
  const [isCustom, setIsCustom] = useState<boolean>(false)
  const refreshSuggestedEvidence = (sto: SuggestionsObject) => {
    setSuggestedEvidence(sto)
  }

  useEffect(() => {
    if (!data) {
      return
    }
    const suggestedEvidence = data.toObject().suggestedEvidence
    setIsCustom(!!(suggestedEvidence && suggestedEvidence.isCustom))
    if (suggestedEvidence) {
      setSuggestedEvidence(getDedupedSuggestionsObject([suggestedEvidence]))
    }
  }, [data])

  useEffect(() => {
    if (isLoading || isValidating) {
      setIsSuggLoading(true)
    } else {
      setIsSuggLoading(false)
    }
  }, [isLoading, isValidating])

  const onSave = async (newSuggestedEvidence: string) => {
    if (convertSuggestionsToText(suggestedEvidence) === newSuggestedEvidence) {
      return
    }

    await updateSuggestedEvidence({
      controlId: controlId,
      suggestedEvidence: newSuggestedEvidence,
    })
  }

  return (
    <ControlSubsectionAccordionItemComponent
      eventKey={eventKey}
      controlModelId={modelId}
      controlId={controlId}
      complianceFrameworkIds={complianceFrameworks}
      icon={<SubsectionAccordionEvidenceIcon />}
      title="Required Evidence"
      notes={getNotesCopy(isSuggLoading, isCustom)}
      body={
        isSuggLoading ? (
          <GuidanceLoading />
        ) : (
          <SafeEditField
            value={
              isCustom
                ? getCustomSuggestionText(suggestedEvidence)
                : convertSuggestionsToText(suggestedEvidence)
            }
            onSave={onSave}
            warning={<EditGuidanceWarning />}
            overrideViewState={
              <SubsectionSuggestion
                isCustom={isCustom}
                suggestions={suggestedEvidence}
                controlId={controlId}
              />
            }
            onClickConfirmationModal={onClickConfirmationModal}
          />
        )
      }
      suggestionType={SUGGESTION_TYPES.EVIDENCE}
      suggestion={suggestedEvidence}
      refresh={refreshSuggestedEvidence}
      isUpdating={isSuggLoading}
    />
  )
}

export const EditGuidanceWarning = (): JSX.Element => (
  <WarningP>
    Great care should be taken when editing. The Trustero Customer Success and
    GRC teams are happy to help with this process. For more information, see the{' '}
    <SuggestionKbLink />
  </WarningP>
)

const TestLengthWarning = (): JSX.Element => (
  <WarningP>
    Warning: Trustero does not usually recommend having more than four tests.
    For more information, see the <SuggestionKbLink />
  </WarningP>
)

const SuggestionKbLink = (): JSX.Element => (
  <a
    onClick={(e) => e.stopPropagation()}
    target="_blank"
    rel="noreferrer"
    href={HUBSPOT.REQUIRED_EVIDENCE_AND_TEST_PROCEDURES}
  >
    Trustero KB.
  </a>
)
